import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, Transition as _Transition, withCtx as _withCtx, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock } from "vue"

import { WebSocketProvider } from '@ethersproject/providers';
import {
  onBeforeMount, ref, watch,
} from 'vue';
import BurnCompleteBody from '@/components/BurnCompleteBody.vue';
import Spinner from '@/components/Spinner.vue';
import NotFound from '@/components/NotFound.vue';
import { useAccount } from '@/composables/useAccount';
import { AuthState } from '@/store/modules/account.store';
import { ModuleAccountModule } from '@/store/modules/account.store';
import {
  MBurnRedeemCompleteWidgetPropsDefinition,
  useProvideAll,
  MBurnRedeemCompleteWidgetProps,
} from './MBurnRedeemCompleteWidgetProps';
import { ModuleBRModule } from '@/store/modules/br.store';
import { AUTHENTICATED, UNAUTHENTICATED } from '@/common/constants';
import { DATA_WIDGET_VALUE } from '@/common/constants';
import BurnRedeemExtensionContractCore from '@/classes/burnRedeemExtensionCore';
import ManifoldDataClient from '@manifoldxyz/oauth-data-client';
import { useBR } from '@/composables/useBR';
import { detectManifoldEthereumProvider } from '@manifoldxyz/frontend-provider-utils';
import { CHAIN_CHANGED, PROVIDER_CHANGED } from '@manifoldxyz/frontend-provider-types';


// Define & set all props, then provide() them to all children

export default /*#__PURE__*/_defineComponent({
  __name: 'MBurnRedeemCompleteWidget',
  props: MBurnRedeemCompleteWidgetPropsDefinition,
  setup(__props) {

const props = __props;


useProvideAll(props as MBurnRedeemCompleteWidgetProps);

const {
  burn, studioInstance, redeemAssetMetadata, redeemSpec,
  extensionContract, redeemContractAddress, burnRedeemIndex,
} = useBR();

const noBurnRedeemError = ref(false);
const refreshTimeout = ref<number>(0);

// @dev: when a provider changes or is available, refresh the data just in case
const { isProviderAvailable } = useAccount();
watch(isProviderAvailable, async () => {
  if (isProviderAvailable.value) {
    /**
     * Refresh all chain dependant data in the store
     */
    ModuleBRModule.setExtensionAddressSpecVersionAndFees(
      studioInstance.value?.publicData?.extensionAddress || props.extensionAddress,
    );
    ModuleBRModule.setRedeemContractAddress(
      studioInstance.value?.publicData?.redeemContractAddress ||
        props.redeemContractAddress,
    );
    ModuleBRModule.setBurnRedeemIndex(
      studioInstance.value?.publicData?.burnIndex.toString() || props.burnRedeemIndex,
    );

    // since the BR extension is AL'd by the manifold provider we can call this instantly
    await ModuleBRModule.refreshBurnRedeemOnChain();
    await ModuleBRModule.refreshRedeemAssetData();
  }
});

const validateFallbackProvider = async (fallbackProvider: string) => {
  const provider = new WebSocketProvider(fallbackProvider);
  const timeOut = new Promise<Response>((resolve, reject) =>
    setTimeout(() => reject(new Error('timeout')), 2000),
  );
  // provider.ready check if connection to provider is valid, timeout after 2s
  await Promise.race([provider.ready, timeOut]);
};

onBeforeMount(async () => {
  ModuleAccountModule.setAuthState(AuthState.UNKNOWN);

  /**
   * Setup window listeners for EthereumProvider and Wallet Connect
   */

  window.addEventListener(
    CHAIN_CHANGED,
    ModuleAccountModule.detectChainChanged,
  );
  window.addEventListener(
    PROVIDER_CHANGED,
    ModuleAccountModule.detectProviderChanged,
  );
  window.addEventListener(AUTHENTICATED, async (event) => {
    const rerefdEvent = (event as any); // eslint-disable-line

    if (rerefdEvent.detail && rerefdEvent.detail.client) {
      ModuleAccountModule.setClient(rerefdEvent.detail.client as ManifoldDataClient);
    }

    if (rerefdEvent.detail && rerefdEvent.detail.address) {
      ModuleAccountModule.setWalletAddress(rerefdEvent.detail.address);
    }

    ModuleAccountModule.detectProviderChanged();
    ModuleAccountModule.detectENSName();
    ModuleAccountModule.detectChainChanged();

    if (ModuleAccountModule.walletAddress) {
      await ModuleAccountModule.setAuthState(AuthState.LOGGED_IN);
    } else {
      ModuleAccountModule.setAuthState(AuthState.GUEST);
    }

    try {
      // these next two lines throw if we're on no-provider/WC provider session
      const listeningContract = (
        extensionContract.value as BurnRedeemExtensionContractCore
      ).getListeningContract();
      listeningContract.on('BurnRedeemMint', (creatorContractAddress, index) => {
        if (
          creatorContractAddress.toLowerCase() ===
            redeemContractAddress.value.toLowerCase() &&
          index.toString() === burnRedeemIndex.value
        ) {
          ModuleBRModule.refreshBurnRedeemOnChain();
        }
      });
    } catch {
      // On WC-type providers (or no provider at all) we cannot listen for
      // on chain events as they happen so instead we refresh on an interval
      window.clearInterval(refreshTimeout.value);
      refreshTimeout.value = window.setInterval(ModuleBRModule.refreshBurnRedeemOnChain, 30000);
    }
  });
  window.addEventListener(UNAUTHENTICATED, async () => {
    await ModuleAccountModule.setAuthState(AuthState.GUEST);
  });

  /**
   * Setup global store and initialize EthereumProvider
   */

  if (props.id) {
    await ModuleBRModule.refreshStudioInstance(props.id);
  }

  let fallbackProvider;
  if (props.fallbackProvider) {
    try {
      // use the user's fallback provider if available
      await validateFallbackProvider(props.fallbackProvider);
      fallbackProvider = props.fallbackProvider;
    } catch {}
  }

  await detectManifoldEthereumProvider({ initialized: false });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((window.ManifoldEthereumProvider as any)._isInitialized !== undefined) {
    // Legacy version of connect widget
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (window.ManifoldEthereumProvider as any).initialize(props.network, fallbackProvider);
  } else {
    await window.ManifoldEthereumProvider.initialize({
      network: props.network,
      fallbackHost: fallbackProvider,
      browserProviderTimeout: 500,
    });
  }

  // @dev: update the store so that refreshBurnRedeemOnChain() will succeed on wrong network
  ModuleBRModule.setExtensionAddressSpecVersionAndFees(
    studioInstance.value?.publicData?.extensionAddress || props.extensionAddress,
  );
  ModuleBRModule.setRedeemContractAddress(
    studioInstance.value?.publicData?.redeemContractAddress || props.redeemContractAddress,
  );
  ModuleBRModule.setBurnRedeemIndex(
    studioInstance.value?.publicData?.burnIndex.toString() || props.burnRedeemIndex,
  );
  ModuleBRModule.setRedeemSpec(redeemSpec.value || 'erc1155');
  // since the BR extension is AL'd by the manifold provider we can call this instantly
  await ModuleBRModule.refreshBurnRedeemOnChain();
  // then use fetch on the data to get recent asset info
  await ModuleBRModule.refreshRedeemAssetData();

  // @dev: if there is a specific start date, set UX to refresh when it starts
  if (burn.value && burn.value.startDate) {
    const startDate = burn.value.startDate * 1000;
    const now = Date.now();
    const timeLeft = startDate - now;
    if (timeLeft > 0) {
      window.setInterval(ModuleBRModule.refreshBurnRedeemOnChain, timeLeft + 1);
    }
  }

  // @dev: if there is a specific end date, set UX to refresh when it ends
  if (burn.value && burn.value?.endDate) {
    const endDate = burn.value.endDate * 1000;
    const now = Date.now();
    const timeLeft = endDate - now;
    if (timeLeft > 0) {
      window.setInterval(ModuleBRModule.refreshBurnRedeemOnChain, timeLeft + 1);
    }
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(_unref(DATA_WIDGET_VALUE))
  }, [
    (_unref(redeemAssetMetadata) && _unref(burn) && _unref(studioInstance))
      ? (_openBlock(), _createBlock(BurnCompleteBody, {
          key: 0,
          "burn-redeem-studio-instance": _unref(studioInstance),
          "redeem-asset-metadata": _unref(redeemAssetMetadata)
        }, null, 8, ["burn-redeem-studio-instance", "redeem-asset-metadata"]))
      : (!noBurnRedeemError.value)
        ? (_openBlock(), _createBlock(_Transition, {
            key: 1,
            id: "widget-loading",
            name: "fade"
          }, {
            default: _withCtx(() => [
              _createVNode(Spinner)
            ]),
            _: 1
          }))
        : (_openBlock(), _createBlock(NotFound, { key: 2 }))
  ], 2))
}
}

})